import React from 'react';
import { motion } from 'framer-motion';
import { FiPhone, FiFileText, FiCheck, FiChevronRight, FiMic, FiZap, FiStar, } from 'react-icons/fi';
import type { Amp } from '../chat/chat.types';
import NowCertsLogo from '../../assets/images/nowcertlogo.png';
import '../../styles/Ams.css';
interface AmsRequiredPageProps {
    featureName?: string;
    amp?: Amp;
}
const VOICE_FEATURES = [
    {
        icon: <FiPhone size={15}/>,
        color: '#8b5cf6',
        bg: 'rgba(139,92,246,0.14)',
        label: 'Live Policy Lookup',
        desc: 'Instant policy info surfaced during every call',
    },
    {
        icon: <FiFileText size={15}/>,
        color: '#06b6d4',
        bg: 'rgba(6,182,212,0.14)',
        label: 'Service Requests',
        desc: 'Auto-submit requests directly to your AMS',
    },
    {
        icon: <FiMic size={15}/>,
        color: '#f97316',
        bg: 'rgba(249,115,22,0.14)',
        label: 'AI Voice Agents',
        desc: 'Handle inbound calls 24/7 with smart AI',
    },
    {
        icon: <FiCheck size={15}/>,
        color: '#22c55e',
        bg: 'rgba(34,197,94,0.14)',
        label: 'Call Log Sync',
        desc: 'Every call automatically logged to client records',
    },
];
const PROMPT_COLORS = [
    { color: '#8b5cf6', bg: 'rgba(139,92,246,0.14)', icon: <FiZap size={15}/> },
    { color: '#06b6d4', bg: 'rgba(6,182,212,0.14)', icon: <FiFileText size={15}/> },
    { color: '#f97316', bg: 'rgba(249,115,22,0.14)', icon: <FiStar size={15}/> },
    { color: '#22c55e', bg: 'rgba(34,197,94,0.14)', icon: <FiCheck size={15}/> },
];
const AmsRequiredPage: React.FC<AmsRequiredPageProps> = ({ featureName = 'This feature', amp, }) => {
    const isVoice = featureName.toLowerCase().includes('voice');
    const features = isVoice || !amp
        ? VOICE_FEATURES
        : [amp.prompt1, amp.prompt2, amp.prompt3, amp.prompt4]
            .filter((p): p is string => !!p)
            .map((prompt, i) => ({
            icon: PROMPT_COLORS[i % PROMPT_COLORS.length].icon,
            color: PROMPT_COLORS[i % PROMPT_COLORS.length].color,
            bg: PROMPT_COLORS[i % PROMPT_COLORS.length].bg,
            label: prompt.length > 50 ? prompt.slice(0, 50).trimEnd() + '…' : prompt,
            desc: '',
        }));
    return (<div className="ams-required">
      <motion.div className="ams-required__card" initial={{ opacity: 0, y: 28, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}>
        <div className="ams-required__badge">
          <span style={{ fontSize: 10 }}>◈</span>
          AMS Integration Required
        </div>

        <motion.div className="ams-required__logo-wrap" initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.1, duration: 0.35, ease: [0.22, 1, 0.36, 1] }}>
          <img src={NowCertsLogo} alt="NowCerts" style={{ width: 170, height: 'auto', display: 'block' }}/>
        </motion.div>

        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 'var(--space-3)',
        }}>
          <h2 className="ams-required__title">Connect Your NowCerts Account</h2>
          <p className="ams-required__subtitle">
            {featureName} requires an active NowCerts AMS connection. Link your
            account to unlock the full capabilities of this feature — powered by
            your live AMS data in real time.
          </p>
        </div>

        <motion.div className="ams-required__features" initial="hidden" animate="visible" variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.07, delayChildren: 0.15 } },
        }}>
          {features.map((f) => (<motion.div key={f.label} className="ams-required__feature" variants={{
                hidden: { opacity: 0, y: 10 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.28 } },
            }}>
              <div className="ams-required__feature-icon" style={{ background: f.bg, color: f.color }}>
                {f.icon}
              </div>
              <div className="ams-required__feature-text">
                <span className="ams-required__feature-label">{f.label}</span>
                <span className="ams-required__feature-desc">{f.desc}</span>
              </div>
            </motion.div>))}
        </motion.div>

        <div className="ams-required__divider"/>

        <motion.div className="ams-required__actions" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.28 }}>
          <a href="https://www.nowcerts.com/" target="_blank" rel="noopener noreferrer" className="ams-btn-primary">
            Log In to NowCerts
            <span style={{ fontSize: 13, lineHeight: 1 }}>↗</span>
          </a>
          <a href="https://www.nowcerts.com/signup" target="_blank" rel="noopener noreferrer" className="ams-btn-secondary">
            Create Free Account
            <FiChevronRight size={14}/>
          </a>
        </motion.div>

        <p className="ams-required__note">
          Already have an account?&nbsp;
          <a href="https://www.nowcerts.com/" target="_blank" rel="noopener noreferrer">
            Sign in to NowCerts
          </a>
          &nbsp;then log back into Insurance Agency AI to activate your connection.
        </p>

        <div className="ams-required__powered">
          <span style={{ fontSize: 12 }}>◈</span>
          Powered by&nbsp;<strong>NowCerts AMS</strong>&nbsp;— trusted by
          thousands of independent insurance agencies
        </div>
      </motion.div>
    </div>);
};
export default AmsRequiredPage;
